import React, { useEffect, useMemo, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import style from "./styles.module.scss"
import { Message } from "./Message"
import { DateDivider } from "./DateDivider"
import { isTheSameDate } from "./helpers"

import { getChatList, getMessages } from "modules/Chat/utils/api"
import { MessageStatus } from "modules/Chat/utils/interfaces"
import { chatSelector } from "utils/store/selectors/chat"
import { AppDispatch } from "utils/store/store"
import { useSocket } from "utils/contexts/SocketContext"
import { addMessage, updateChat, updateMessage } from "utils/store/slices"
import { userSelector } from "utils/store/selectors/user"
import { ROLES } from "utils/constants"
import { Textarea } from "ui-kit/components-branded/FormControls/Textarea"
import { Icon } from "components"

export function Dialog() {
    const dispatch = useDispatch<AppDispatch>()
    const socket = useSocket()
    const [newMessage, setNewMessage] = useState("")
    const { clientHash, psyHash } = useSelector(userSelector)
    const { token, activeChatId, messages, role, chats } = useSelector(chatSelector)
    const messagesEndRef = useRef<HTMLDivElement | null>(null)
    const prevMessagesLength = useRef(messages.length)
    const userHash = role === ROLES.CLIENT ? clientHash : psyHash

    useEffect(() => {
        if (!socket || !activeChatId) return

        socket.offReconnect(resendPendingMessages)
        socket.onReconnect(resendPendingMessages)

        return () => {
            socket.offReconnect(resendPendingMessages)
        }
    }, [socket, activeChatId])

    useEffect(() => {
        if (token && activeChatId) {
            void dispatch(getMessages(token, activeChatId))

            const chatIndex = chats.findIndex((chat) => chat.chatId === activeChatId)
            if (chatIndex === -1) return

            if (chats[chatIndex].unreadCount > 0) {
                const updatedChat = {
                    ...chats[chatIndex],
                    unreadCount: 0,
                }
                dispatch(updateChat(updatedChat))
            }
        }
    }, [activeChatId])

    useEffect(() => {
        if (!socket || !activeChatId) return

        const messagesToUpdate = messages.filter(
            (msg) => msg.status === MessageStatus.CREATED && msg.externalUserId !== userHash
        )

        if (role) {
            void dispatch(getChatList(role))
        }

        if (!messagesToUpdate.length) return

        messagesToUpdate.forEach((msg) => {
            socket.send({
                id: msg.id,
                chatId: msg.chatId,
                action: "viewed",
            })

            dispatch(
                updateMessage({
                    id: msg.id,
                    status: MessageStatus.VIEWED,
                })
            )
        })
    }, [activeChatId, messages])

    const sortedMessages = useMemo(() => {
        return messages.length
            ? [...messages].sort(
                  (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
              )
            : []
    }, [messages])

    useEffect(() => {
        if (messages.length > prevMessagesLength.current) {
            messagesEndRef.current?.scrollIntoView()
        }
    }, [messages])

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView()
    }, [])

    useEffect(() => {
        if (token && activeChatId) {
            void dispatch(getMessages(token, activeChatId))

            if (role) {
                void dispatch(getChatList(role))
            }
        }
    }, [activeChatId])

    const resendPendingMessages = () => {
        const sentMessages = messages.filter((msg) => msg.status === MessageStatus.SENT)

        if (!sentMessages.length || !socket) return

        sentMessages.forEach((msg) => {
            socket.send({
                text: msg.text,
                chatId: msg.chatId,
                hash: msg.hash,
            })
        })
    }

    const sendMessage = () => {
        if (socket && newMessage.trim() && activeChatId && userHash) {
            const messageUuid = crypto.randomUUID()
            const formattedTimestamp = new Date().toISOString()

            const tempMessage = {
                chatId: activeChatId,
                externalUserId: userHash,
                timestamp: formattedTimestamp,
                text: newMessage,
                hash: messageUuid,
                status: MessageStatus.SENT,
            }

            dispatch(addMessage(tempMessage))

            socket.send({
                text: newMessage,
                chatId: activeChatId,
                hash: messageUuid,
            })

            setNewMessage("")
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault()
            sendMessage()
        }
    }

    const handleChange = (value: string) => {
        setNewMessage(value)
    }

    return (
        <div className={style.container}>
            <div className={style.scrollBox}>
                <div className={style.content}>
                    {sortedMessages.map((message, key: number) => (
                        <div key={message.id || message.hash}>
                            {(key === 0 ||
                                (!isTheSameDate(
                                    message.timestamp,
                                    sortedMessages[key - 1]?.timestamp
                                ) &&
                                    sortedMessages[key - 1]?.text)) && (
                                <DateDivider date={new Date(message.timestamp)} />
                            )}

                            <Message
                                message={message}
                                key={message.text || message.hash}
                                lastMessage={
                                    message.externalUserId !==
                                    sortedMessages[key + 1]?.externalUserId
                                }
                            />
                        </div>
                    ))}
                    <div ref={messagesEndRef} />
                </div>
            </div>
            <div className={style.inputBox}>
                <Textarea
                    fieldClassName={style.field}
                    value={newMessage}
                    placeholder="Напишите сообщение..."
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    name={"message"}
                />
                <div className={style.sendButton} onClick={sendMessage}>
                    <Icon type="arrow-short-right" size={16} />
                </div>
            </div>
        </div>
    )
}
